/**
 * Lykke on the left: the brand's account manager. Write, dictate, attach a brief, or call her; she fills the document
 * with what you say (the server keeps only values your words state) and asks for what is still missing.
 *
 * The input is the universal composer (shell/AgentComposer). In the new layout this conversation is a mode of Lykke's
 * panel (`inPanel`: no header of its own, the panel has one); a brief Lykke was handed elsewhere (shell/handoff.ts)
 * is read here as soon as the order opens.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import { AGENT, speechSupported } from '@/cowork/proposals/ProposalBuilder';
import { RealtimeCall } from '@/cowork/proposals/realtimeCall';
import { voiceErrorText } from '@/cowork/voice/voiceSession';
import { AgentComposer, type CallPhase } from '@/cowork/shell/AgentComposer';
import { onHandoff, takeHandoff } from '@/cowork/shell/handoff';
import { ordersApi, type ChatTurn, type ClientOrder } from './api';
import { anchorOf, scrollToInDoc } from './model';

type Msg = ChatTurn;

export function LykkePanel({ order, readOnly, onOrder, className, inPanel = false }: { order: ClientOrder; readOnly: boolean; onOrder: (o: ClientOrder) => void; className?: string; inPanel?: boolean }) {
  const id = order.id;
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [busy, setBusy] = useState<null | 'chat' | 'import'>(null);
  const logged = useRef<number | null>(null);
  const opened = useRef(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const orderRef = useRef(order); orderRef.current = order;

  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' }); }, [msgs.length, busy]);

  const sendChat = useCallback(async (message: string, history: Msg[]) => {
    if (message.trim()) setMsgs((m) => [...m, { role: 'user', text: message.trim() }]);
    setBusy('chat');
    try {
      const r = await ordersApi.chat(id, message, history.filter((m) => !m.kind).slice(-16).map((m) => ({ role: m.role, text: m.text })));
      setMsgs((m) => [...m, { role: 'lykke', text: r.say, ...(r.applied.length ? { applied: r.applied } : {}) }]);
      if (r.applied.length) onOrder(r.order);
    } catch (e) {
      setMsgs((m) => [...m, { role: 'lykke', text: `Sorry, that did not go through: ${(e as Error).message}` }]);
    } finally { setBusy(null); }
  }, [id, onOrder]);

  // The conversation is kept on the server: a reload shows it again. A new order: Lykke opens with the first question.
  useEffect(() => {
    let live = true;
    ordersApi.chatLog(id).then((r) => {
      if (!live) return;
      logged.current = r.turns.length;
      if (r.turns.length) { setMsgs(r.turns); opened.current = true; }
      else if (!readOnly && !opened.current) { opened.current = true; void sendChat('', []); }
    }).catch(() => { logged.current = 0; });
    return () => { live = false; };
  }, [id]);
  useEffect(() => {
    if (logged.current === null || msgs.length <= logged.current) return;
    const fresh = msgs.slice(logged.current); logged.current = msgs.length;
    void ordersApi.logChat(id, fresh).catch(() => undefined);
  }, [msgs, id]);

  const importFiles = async (t: string, fs: File[]) => {
    const label = fs.length ? fs.map((f) => f.name).join(', ') : 'your text';
    setMsgs((m) => [...m, { role: 'user', text: t.trim() || `(Sent ${label})`, kind: 'import' }]);
    setBusy('import');
    try {
      const r = await ordersApi.intake(id, t, fs);
      setMsgs((m) => [...m, { role: 'lykke', text: r.say, ...(r.applied.length ? { applied: r.applied } : {}) }]);
      onOrder(r.order);
    } catch (e) { setMsgs((m) => [...m, { role: 'lykke', text: `I could not read that: ${(e as Error).message}` }]); }
    finally { setBusy(null); }
  };
  const send = (raw: string, fs: File[]): boolean | void => {
    if (busy) return false;
    if (fs.length || raw.trim().length > 400) { void importFiles(raw, fs); return; }
    const t = raw.trim(); if (!t) return false;
    void sendChat(t, msgs);
  };

  // A brief handed to Lykke on another page (or in her panel) is read into this order as soon as it opens.
  const importRef = useRef(importFiles); importRef.current = importFiles;
  useEffect(() => {
    if (readOnly) return;
    const take = () => { const h = takeHandoff('kickoff'); if (h) void importRef.current(h.text, h.files); };
    take();
    return onHandoff((kind) => { if (kind === 'kickoff') take(); });
  }, [readOnly, id]);

  // ── Live call: Lykke speaks; her tools write through the server, grounded in what was said on the call ──
  const [call, setCall] = useState<{ on: boolean; phase: string; live: string; agentLive: string }>({ on: false, phase: 'idle', live: '', agentLive: '' });
  const rtRef = useRef<RealtimeCall | null>(null);
  const heard = useRef<string[]>([]);
  const startCall = async () => {
    if (rtRef.current) return;
    const write = async (body: { fields?: unknown; blocks?: unknown }) => {
      const r = await ordersApi.callWrite(id, { ...body, said: heard.current.slice(-8).join('\n') });
      onOrder(r.order);
      if (r.saved.length) setMsgs((m) => [...m, { role: 'lykke', text: `Wrote ${r.saved.map((s) => s.label).join(', ')}.`, applied: r.saved }]);
      return { saved: r.saved.map((s) => s.label), notSaved: r.rejected.map((x) => x.id), stillMissing: r.stillMissing };
    };
    const rt = new RealtimeCall([
      { name: 'set_fields', description: 'Write values the client just said into the order. Keys are field ids from the catalog; values as said (digits, yyyy-mm-dd, catalog options). Pass each as {"value": ..., "quote": "<their words>"}.', parameters: { type: 'object', properties: { fields: { type: 'object', additionalProperties: true } }, required: ['fields'] }, execute: (a) => write({ fields: a.fields }) },
      { name: 'set_blocks', description: 'Add, change or remove building blocks the client just described (kind ugc | posts | boost, platform, creators, band, videos, reels, stories, tiktoks, adBudget, goal, quote).', parameters: { type: 'object', properties: { blocks: { type: 'array', items: { type: 'object', additionalProperties: true } } }, required: ['blocks'] }, execute: (a) => write({ blocks: a.blocks }) },
      { name: 'get_document', description: 'What the order holds now and what is still missing.', parameters: { type: 'object', properties: {}, required: [] }, execute: async () => ({ values: orderRef.current.values, blocks: orderRef.current.blocks.map((b) => ({ key: b.key, title: b.title, creators: b.creators, gaps: b.gaps })), stillMissing: orderRef.current.gaps.map((g) => g.label) }) },
    ], {
      onStatus: (st) => { if (st === 'ended' || st === 'error') { setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); rtRef.current = null; return; } setCall((c) => ({ ...c, on: true, phase: st })); },
      onTranscript: (role, t, final) => {
        if (role === 'user') { if (final && t.trim()) { heard.current.push(t.trim()); setMsgs((m) => [...m, { role: 'user', text: t.trim() }]); setCall((c) => ({ ...c, live: '' })); } else setCall((c) => ({ ...c, live: t })); return; }
        if (final) { if (t.trim()) setMsgs((m) => [...m, { role: 'lykke', text: t.trim() }]); setCall((c) => ({ ...c, agentLive: '' })); } else setCall((c) => ({ ...c, agentLive: t }));
      },
      onError: (m) => toast.error(m),
    });
    rtRef.current = rt;
    setCall({ on: true, phase: 'connecting', live: '', agentLive: '' });
    // The offer goes to the mint route: the server connects the call and keeps its voice session (voiceSession.ts).
    try { await rt.connect((sdp) => ordersApi.realtimeSecret(id, sdp), undefined, (secret) => ({ instructions: secret.instructions, voice: secret.voice, opening: 'Open the call now: no greeting, ask for the first thing still missing, 20 words at most.' })); }
    catch (e) { rtRef.current = null; setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); toast.error(voiceErrorText(e, 'The call did not connect.')); }
  };
  const endCall = () => { rtRef.current?.end(); rtRef.current = null; setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); };
  useEffect(() => () => { rtRef.current?.end(); }, []);

  const jump = (fid: string) => scrollToInDoc(fid === 'blocks' ? 'ck-field-blocks' : anchorOf(fid), 'center');
  const callPhase: CallPhase = !call.on ? 'off' : call.phase === 'connecting' ? 'connecting' : call.phase === 'speaking' ? 'speaking' : call.phase === 'tool' ? 'working' : 'listening';
  const status = call.on ? ({ speaking: 'Talking', listening: 'Listening', connecting: 'Connecting', tool: 'Writing' } as Record<string, string>)[call.phase] || 'On the call' : busy === 'import' ? 'Reading' : busy ? 'Writing' : 'Your account manager at Beatly';

  return (
    <aside aria-label="Lykke, your account manager" className={cn('relative flex min-h-0 flex-col', inPanel ? 'flex-1' : 'bg-white', className)}>
      {!inPanel && <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
        <span className={cn('relative h-10 w-10 flex-none overflow-hidden rounded-full bg-[#2e024c]', call.on && 'ring-2 ring-[#a9f600] ring-offset-2')}>
          {call.on ? <video src={AGENT.video} poster={AGENT.photo} autoPlay loop muted playsInline className="h-full w-full object-cover" aria-hidden /> : <img src={AGENT.photo} alt="" className="h-full w-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>Lykke</div>
          <div className="truncate text-[12px] text-[#667085]">{status}</div>
        </div>
      </div>}

      <div ref={threadRef} className="flex min-h-[180px] flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 [scrollbar-width:none]" aria-live="polite">
        {msgs.map((m, i) => m.role === 'user'
          ? <p key={i} className="max-w-[88%] self-end whitespace-pre-wrap rounded-2xl rounded-br-md bg-[#be43ff] px-3.5 py-2 text-[14px] leading-relaxed text-white">{m.text}</p>
          : (
            <div key={i} className="flex max-w-[92%] flex-col gap-1.5 self-start">
              <p className="whitespace-pre-wrap rounded-2xl rounded-bl-md bg-[var(--shell-bubble,#f2f4f7)] px-3.5 py-2 text-[14px] leading-relaxed text-[var(--shell-text,#101828)]">{m.text}</p>
              {m.applied?.map((a) => (
                <button key={a.id + a.label} type="button" onClick={() => jump(a.id)} className="inline-flex items-start gap-1.5 text-left text-[12px] text-[#067647] hover:underline">
                  <Check className="mt-px h-3.5 w-3.5 flex-none" aria-hidden /><span>Wrote {a.label}</span>
                </button>
              ))}
            </div>
          ))}
        {call.on && call.agentLive && <p className="max-w-[92%] self-start rounded-2xl bg-[#f9fafb] px-3.5 py-2 text-[14px] text-[#667085]">{call.agentLive}</p>}
        {call.on && call.live && <p className="max-w-[88%] self-end rounded-2xl bg-[#f9f5ff] px-3.5 py-2 text-[14px] italic text-[#667085]">{call.live}</p>}
        {busy && <p className="self-start rounded-2xl rounded-bl-md bg-[#f2f4f7] px-3.5 py-2 text-[13px] text-[#667085]"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" aria-hidden />{busy === 'import' ? 'Reading it' : 'Lykke is writing'}</p>}
      </div>

      {!readOnly && (
        <AgentComposer
          className={inPanel ? 'mx-3 mb-3' : 'border-t border-gray-100 px-3 pb-3 pt-2.5'}
          agentName="Lykke"
          busy={!!busy}
          filesAlone
          maxFiles={8}
          accept=".txt,.md,.csv,.pdf,.rtf,image/*,text/*,application/pdf"
          acceptFile={(f) => /^(image\/|text\/)|pdf|rtf/.test(f.type) || /\.(txt|md|csv|pdf|rtf)$/i.test(f.name)}
          placeholder="Tell Lykke what you want, or drop a brief"
          onSend={(t, fs) => send(t, fs.map((f) => f.file))}
          call={speechSupported ? { phase: callPhase, caption: call.agentLive || call.live || undefined, start: () => void startCall(), end: endCall } : null}
        />
      )}
    </aside>
  );
}
